import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Card, Select, MenuItem, 
  FormControl, InputLabel, TextField, Button, Alert,
  Accordion, AccordionSummary, AccordionDetails, Grid,
  Chip, Divider, IconButton, Tooltip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AppSettingsAltIcon from '@mui/icons-material/AppSettingsAlt';
import PaletteIcon from '@mui/icons-material/Palette';
import ChatIcon from '@mui/icons-material/Chat';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import LanguageIcon from '@mui/icons-material/Language';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useAppContext } from '../context/AppContext';
import useTheme from '../hooks/useTheme';
import DocumentManager from './DocumentManager';

export default function Settings() {
  const { state, actions } = useAppContext();
  const theme = useTheme();
  const [saveStatus, setSaveStatus] = useState({ show: false, type: 'success', message: '' });
  const [promptData, setPromptData] = useState({ prompt: '', prompt_type: '' });
  const [promptLoading, setPromptLoading] = useState(false);
  const [promptSaving, setPromptSaving] = useState(false);
  
  // Report prompt state
  const [reportPromptData, setReportPromptData] = useState({ prompt: '', prompt_type: '' });
  const [reportPromptLoading, setReportPromptLoading] = useState(false);
  const [reportPromptSaving, setReportPromptSaving] = useState(false);
  
  // Report guidance state
  const [reportGuidanceData, setReportGuidanceData] = useState({ 
    guidance: 'Enter your report guidance text here...', 
    guidance_type: "REPORT_GUIDANCE" 
  });
  const [reportGuidanceLoading, setReportGuidanceLoading] = useState(false);
  const [reportGuidanceSaving, setReportGuidanceSaving] = useState(false);
  
  // Accordion expansion states
  const [appSettingsExpanded, setAppSettingsExpanded] = useState(true);
  const [promptCustomizationExpanded, setPromptCustomizationExpanded] = useState(true);
  const [reportPromptCustomizationExpanded, setReportPromptCustomizationExpanded] = useState(true);
  const [themeSettingsExpanded, setThemeSettingsExpanded] = useState(true);
  const [documentManagementExpanded, setDocumentManagementExpanded] = useState(false);

  // API functions for prompt management
  const fetchPrompt = async () => {
    setPromptLoading(true);
    try {
      const response = await fetch('https://api.uxhq.com/prompts');
      if (response.ok) {
        const data = await response.json();
        setPromptData(data);
      } else {
        console.error('Failed to fetch prompt');
      }
    } catch (error) {
      console.error('Error fetching prompt:', error);
    } finally {
      setPromptLoading(false);
    }
  };

  const updatePrompt = async (newPrompt) => {
    setPromptSaving(true);
    try {
      const response = await fetch('https://api.uxhq.com/prompts', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: newPrompt, prompt_type: "CONVERSATION PROMPT" }),
      });
      if (response.ok) {
        const data = await response.json();
        setPromptData(data);
        setSaveStatus({ show: true, type: 'success', message: 'Prompt updated successfully!' });
        setTimeout(() => setSaveStatus({ show: false, type: 'success', message: '' }), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update prompt:', errorData);
        setSaveStatus({ show: true, type: 'error', message: errorData.detail || 'Failed to update prompt!' });
        setTimeout(() => setSaveStatus({ show: false, type: 'error', message: '' }), 3000);
      }
    } catch (error) {
      console.error('Error updating prompt:', error);
      setSaveStatus({ show: true, type: 'error', message: 'Error updating prompt!' });
      setTimeout(() => setSaveStatus({ show: false, type: 'error', message: '' }), 3000);
    } finally {
      setPromptSaving(false);
    }
  };

  // API functions for report prompt management
  const fetchReportPrompt = async () => {
    setReportPromptLoading(true);
    try {
      const response = await fetch('https://api.uxhq.com/report_prompts');
      if (response.ok) {
        const data = await response.json();
        setReportPromptData(data);
      } else {
        console.error('Failed to fetch report prompt');
      }
    } catch (error) {
      console.error('Error fetching report prompt:', error);
    } finally {
      setReportPromptLoading(false);
    }
  };

  const updateReportPrompt = async (newPrompt) => {
    setReportPromptSaving(true);
    try {
      const response = await fetch('https://api.uxhq.com/report_prompts', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt: newPrompt, prompt_type: "REPORT GENERATION PROMPT" }),
      });
      if (response.ok) {
        const data = await response.json();
        setReportPromptData(data);
        setSaveStatus({ show: true, type: 'success', message: 'Report prompt updated successfully!' });
        setTimeout(() => setSaveStatus({ show: false, type: 'success', message: '' }), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update report prompt:', errorData);
        setSaveStatus({ show: true, type: 'error', message: errorData.detail || 'Failed to update report prompt!' });
        setTimeout(() => setSaveStatus({ show: false, type: 'error', message: '' }), 3000);
      }
    } catch (error) {
      console.error('Error updating report prompt:', error);
      setSaveStatus({ show: true, type: 'error', message: 'Error updating report prompt!' });
      setTimeout(() => setSaveStatus({ show: false, type: 'error', message: '' }), 3000);
    } finally {
      setReportPromptSaving(false);
    }
  };

  // API functions for report guidance management
  const fetchReportGuidance = async () => {
    setReportGuidanceLoading(true);
    try {
      const response = await fetch('https://api.uxhq.com/reportguidance/content');
      if (response.ok) {
        const text = await response.text();
        // Clean up the text content - remove any binary artifacts
        const cleanText = text
          .replace(/PK[\x00-\x1f\x7f-\x9f]*/g, '') // Remove PK headers
          .replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F-\x9F]/g, '') // Remove control characters
          .replace(/word\/[^>]*>/g, '') // Remove XML tags
          .replace(/\s+/g, ' ') // Normalize whitespace
          .trim();
        
        setReportGuidanceData({ guidance: cleanText || 'Enter your report guidance text here...', guidance_type: "REPORT_GUIDANCE" });
      } else {
        console.error('Failed to fetch report guidance');
        setReportGuidanceData({ guidance: 'Enter your report guidance text here...', guidance_type: "REPORT_GUIDANCE" });
      }
    } catch (error) {
      console.error('Error fetching report guidance:', error);
      setReportGuidanceData({ guidance: 'Enter your report guidance text here...', guidance_type: "REPORT_GUIDANCE" });
    } finally {
      setReportGuidanceLoading(false);
    }
  };

  const updateReportGuidance = async (newGuidance) => {
    setReportGuidanceSaving(true);
    try {
      // Send the guidance text as Form data since the API expects Form(...)
      const formData = new FormData();
      formData.append('content', newGuidance);
      
      const response = await fetch('https://api.uxhq.com/report_guidance/update_content', {
        method: 'POST',
        body: formData,
      });
      if (response.ok) {
        const data = await response.json();
        setReportGuidanceData({ guidance: newGuidance, guidance_type: "REPORT_GUIDANCE" });
        setSaveStatus({ show: true, type: 'success', message: 'Report guidance updated successfully!' });
        setTimeout(() => setSaveStatus({ show: false, type: 'success', message: '' }), 3000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('Failed to update report guidance:', errorData);
        setSaveStatus({ show: true, type: 'error', message: errorData.detail || 'Failed to update report guidance!' });
        setTimeout(() => setSaveStatus({ show: false, type: 'error', message: '' }), 3000);
      }
    } catch (error) {
      console.error('Error updating report guidance:', error);
      setSaveStatus({ show: true, type: 'error', message: 'Error updating report guidance!' });
      setTimeout(() => setSaveStatus({ show: false, type: 'error', message: '' }), 3000);
    } finally {
      setReportGuidanceSaving(false);
    }
  };

  // Load prompt data on component mount
  useEffect(() => {
    fetchPrompt();
    fetchReportPrompt();
    fetchReportGuidance();
  }, []);

  const handleSave = () => {
    setSaveStatus({ show: true, type: 'success', message: 'Settings saved successfully!' });
    setTimeout(() => setSaveStatus({ show: false, type: 'success', message: '' }), 3000);
  };

  const handleReset = () => {
    actions.resetSettings();
    setSaveStatus({ show: true, type: 'info', message: 'Settings reset to defaults!' });
    setTimeout(() => setSaveStatus({ show: false, type: 'info', message: '' }), 3000);
  };

  const languages = [
    { value: 'en', label: 'English', flag: '🇺🇸' },
    { value: 'es', label: 'Español', flag: '🇪🇸' },
    { value: 'fr', label: 'Français', flag: '🇫🇷' },
    { value: 'de', label: 'Deutsch', flag: '🇩🇪' },
    { value: 'it', label: 'Italiano', flag: '🇮🇹' },
    { value: 'pt', label: 'Português', flag: '🇵🇹' },
    { value: 'ru', label: 'Русский', flag: '🇷🇺' },
    { value: 'zh', label: '中文', flag: '🇨🇳' },
    { value: 'ja', label: '日本語', flag: '🇯🇵' },
    { value: 'ko', label: '한국어', flag: '🇰🇷' }
  ];

  const timezones = [
    { value: 'UTC', label: 'UTC (GMT+0)', offset: '+00:00' },
    { value: 'America/New_York', label: 'Eastern Time', offset: '-05:00' },
    { value: 'America/Chicago', label: 'Central Time', offset: '-06:00' },
    { value: 'America/Denver', label: 'Mountain Time', offset: '-07:00' },
    { value: 'America/Los_Angeles', label: 'Pacific Time', offset: '-08:00' },
    { value: 'Europe/London', label: 'London', offset: '+00:00' },
    { value: 'Europe/Paris', label: 'Paris', offset: '+01:00' },
    { value: 'Asia/Tokyo', label: 'Tokyo', offset: '+09:00' },
    { value: 'Asia/Shanghai', label: 'Shanghai', offset: '+08:00' },
    { value: 'Australia/Sydney', label: 'Sydney', offset: '+10:00' }
  ];

  // Apply dark theme styles when dark mode is selected
  const isDarkMode = state.theme.mode === 'dark';
  const dynamicTheme = {
    ...theme,
    backgroundColor: isDarkMode ? '#1a1a1a' : theme.backgroundColor,
    cardBackground: isDarkMode ? '#2d2d2d' : theme.cardBackground,
    textColor: isDarkMode ? '#ffffff' : theme.textColor,
    textSecondaryColor: isDarkMode ? '#b0b0b0' : theme.textSecondaryColor,
    borderColor: isDarkMode ? '#404040' : theme.borderColor,
    searchBarColor: isDarkMode ? '#3d3d3d' : theme.searchBarColor,
    tableHeaderColor: isDarkMode ? '#333333' : theme.tableHeaderColor,
  };

  // Enhanced page container style
  const pageContainerStyle = {
    flexGrow: 1,
    width: '100%',
    p: { xs: 2, sm: 3, md: 4 },
    mt: 10,
    background: `linear-gradient(135deg, ${dynamicTheme.backgroundColor} 0%, ${isDarkMode ? '#2a2a2a' : '#f0f2f5'} 100%)`,
    minHeight: '100vh',
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '200px',
      background: `linear-gradient(135deg, ${theme.primaryColor}15 0%, ${theme.secondaryColor}10 100%)`,
      zIndex: 0
    }
  };

  const cardStyle = {
    borderRadius: 4,
    boxShadow: isDarkMode 
      ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 2px 8px rgba(0, 0, 0, 0.2)' 
      : '0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)',
    border: `1px solid ${dynamicTheme.borderColor}`,
    background: dynamicTheme.cardBackground,
    backdropFilter: 'blur(10px)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
    }
  };

  const currentLanguage = languages.find(lang => lang.value === state.app.language) || languages[0];
  const currentTimezone = timezones.find(tz => tz.value === state.app.timezone) || timezones[0];

  return (
    <Box sx={pageContainerStyle}>
      {/* Page Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 4, 
        position: 'relative',
        zIndex: 1
      }}>
        <Box>
          <Typography 
            variant="h3" 
            fontWeight={800} 
            sx={{ 
              color: dynamicTheme.textColor, 
              fontSize: { xs: 28, sm: 32, md: 36 },
              background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              mb: 1
            }}
          >
            Settings
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              color: dynamicTheme.textSecondaryColor,
              fontSize: 16
            }}
          >
            Customize your application preferences and appearance
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="outlined" 
            onClick={handleReset}
            startIcon={<RestartAltIcon />}
            sx={{
              borderColor: dynamicTheme.textSecondaryColor,
              color: dynamicTheme.textSecondaryColor,
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              '&:hover': { 
                borderColor: theme.primaryColor, 
                color: theme.primaryColor,
                background: `${theme.primaryColor}08`
              }
            }}
          >
            Reset
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSave}
            startIcon={<SaveIcon />}
            sx={{
              background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.linkHoverColor})`,
              borderRadius: 2,
              px: 3,
              py: 1.5,
              fontWeight: 600,
              boxShadow: `0 4px 16px ${theme.primaryColor}40`,
              '&:hover': { 
                background: `linear-gradient(135deg, ${theme.linkHoverColor}, ${theme.primaryColor})`,
                boxShadow: `0 6px 20px ${theme.primaryColor}60`
              }
            }}
          >
            Save Settings
          </Button>
        </Box>
      </Box>

      {/* Save Status Alert */}
      {saveStatus.show && (
        <Alert 
          severity={saveStatus.type} 
          sx={{ 
            mb: 4, 
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
          }}
        >
          {saveStatus.message}
        </Alert>
      )}

      {/* Settings Sections */}
      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Discovery Prompt Section */}
        <Box sx={{ flex: '1 1 100%', width: '100%' }}>
          <Card sx={{ ...cardStyle, p: 0, height: '100%' }}>
            <Accordion 
              expanded={true}
              sx={{ 
                boxShadow: 'none', 
                '&:before': { display: 'none' }, 
                background: 'transparent',
                height: '100%',
                '& .MuiAccordionSummary-root': {
                  px: 3,
                  py: 2,
                  background: `linear-gradient(135deg, ${theme.primaryColor}08, ${theme.secondaryColor}08)`,
                  cursor: 'default'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dynamicTheme.textColor }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.secondaryColor})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${theme.primaryColor}30`
                  }}>
                    <ChatIcon sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                    Discovery Prompt
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Typography variant="body2" sx={{ color: dynamicTheme.textSecondaryColor, fontWeight: 500 }}>
                      Conversation Prompt
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={8}
                      value={promptData.prompt}
                      onChange={(e) => setPromptData({ ...promptData, prompt: e.target.value })}
                      placeholder="Enter your conversation prompt here..."
                      disabled={promptLoading}
                      sx={{
                        '& .MuiInputBase-input': { 
                          color: dynamicTheme.textColor,
                          fontSize: 14,
                          lineHeight: 1.6
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: dynamicTheme.borderColor,
                          borderWidth: 2
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                          borderWidth: 2
                        },
                        '& .MuiInputBase-root': {
                          background: dynamicTheme.cardBackground,
                          borderRadius: 2
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor, fontStyle: 'italic' }}>
                      Customize the conversation prompt that will be used for AI interactions
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="outlined" 
                      onClick={fetchPrompt}
                      disabled={promptLoading}
                      sx={{
                        borderColor: dynamicTheme.textSecondaryColor,
                        color: dynamicTheme.textSecondaryColor,
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        '&:hover': { 
                          borderColor: theme.primaryColor, 
                          color: theme.primaryColor,
                          background: `${theme.primaryColor}08`
                        }
                      }}
                    >
                      {promptLoading ? 'Loading...' : 'Reset'}
                    </Button>
                    <Button 
                      variant="contained" 
                      onClick={() => updatePrompt(promptData.prompt)}
                      disabled={promptSaving || !promptData.prompt.trim()}
                      startIcon={<SaveIcon />}
                      sx={{
                        background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.linkHoverColor})`,
                        borderRadius: 2,
                        px: 3,
                        py: 1.5,
                        fontWeight: 600,
                        boxShadow: `0 4px 16px ${theme.primaryColor}40`,
                        '&:hover': { 
                          background: `linear-gradient(135deg, ${theme.linkHoverColor}, ${theme.primaryColor})`,
                          boxShadow: `0 6px 20px ${theme.primaryColor}60`
                        }
                      }}
                    >
                      {promptSaving ? 'Saving...' : 'Save Prompt'}
                    </Button>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Box>

        {/* Report Prompt Customization Section */}
        <Box sx={{ flex: '1 1 100%', width: '100%' }}>
          <Card sx={{ ...cardStyle, p: 0, height: '100%' }}>
            <Accordion 
              expanded={true}
              sx={{ 
                boxShadow: 'none', 
                '&:before': { display: 'none' }, 
                background: 'transparent',
                height: '100%',
                '& .MuiAccordionSummary-root': {
                  px: 3,
                  py: 2,
                  background: `linear-gradient(135deg, ${theme.secondaryColor}08, ${theme.primaryColor}08)`,
                  cursor: 'default'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dynamicTheme.textColor }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.secondaryColor}, ${theme.primaryColor})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${theme.secondaryColor}30`
                  }}>
                    <ChatIcon sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                    Report Customisation
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  {/* Report Prompt Section */}
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ color: dynamicTheme.textColor, textAlign: 'center' }}>
                      Report Prompt
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={reportPromptData.prompt}
                      onChange={(e) => setReportPromptData({ ...reportPromptData, prompt: e.target.value })}
                      placeholder="Enter your report generation prompt here..."
                      disabled={reportPromptLoading}
                      sx={{
                        '& .MuiInputBase-input': { 
                          color: dynamicTheme.textColor,
                          fontSize: 14,
                          lineHeight: 1.6
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: dynamicTheme.borderColor,
                          borderWidth: 2
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                          borderWidth: 2
                        },
                        '& .MuiInputBase-root': {
                          background: dynamicTheme.cardBackground,
                          borderRadius: 2
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor, fontStyle: 'italic', textAlign: 'center' }}>
                      Customize the prompt that will be used for generating AI reports and summaries
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button 
                        variant="outlined" 
                        onClick={fetchReportPrompt}
                        disabled={reportPromptLoading}
                        sx={{
                          borderColor: dynamicTheme.textSecondaryColor,
                          color: dynamicTheme.textSecondaryColor,
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          '&:hover': { 
                            borderColor: theme.primaryColor, 
                            color: theme.primaryColor,
                            background: `${theme.primaryColor}08`
                          }
                        }}
                      >
                        {reportPromptLoading ? 'Loading...' : 'Reset'}
                      </Button>
                      <Button 
                        variant="contained" 
                        onClick={() => updateReportPrompt(reportPromptData.prompt)}
                        disabled={reportPromptSaving || !reportPromptData.prompt.trim()}
                        startIcon={<SaveIcon />}
                        sx={{
                          background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.linkHoverColor})`,
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          boxShadow: `0 4px 16px ${theme.primaryColor}40`,
                          '&:hover': { 
                            background: `linear-gradient(135deg, ${theme.linkHoverColor}, ${theme.primaryColor})`,
                            boxShadow: `0 6px 20px ${theme.primaryColor}60`
                          }
                        }}
                      >
                        {reportPromptSaving ? 'Saving...' : 'Save Prompt'}
                      </Button>
                    </Box>
                  </Box>

                  {/* Divider */}
                  <Divider sx={{ width: '80%', borderColor: dynamicTheme.borderColor }} />

                  {/* Report Guidance Section */}
                  <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <Typography variant="h6" fontWeight={600} sx={{ color: dynamicTheme.textColor, textAlign: 'center' }}>
                      Report Guidance
                    </Typography>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      value={reportGuidanceData.guidance}
                      onChange={(e) => setReportGuidanceData({ ...reportGuidanceData, guidance: e.target.value })}
                      placeholder="Enter your report guidance text here..."
                      disabled={reportGuidanceLoading}
                      sx={{
                        '& .MuiInputBase-input': { 
                          color: dynamicTheme.textColor,
                          fontSize: 14,
                          lineHeight: 1.6
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: dynamicTheme.borderColor,
                          borderWidth: 2
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                          borderWidth: 2
                        },
                        '& .MuiInputBase-root': {
                          background: dynamicTheme.cardBackground,
                          borderRadius: 2
                        }
                      }}
                    />
                    <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor, fontStyle: 'italic', textAlign: 'center' }}>
                      Customize the guidance text that will be used for report generation
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                      <Button 
                        variant="outlined" 
                        onClick={fetchReportGuidance}
                        disabled={reportGuidanceLoading}
                        sx={{
                          borderColor: dynamicTheme.textSecondaryColor,
                          color: dynamicTheme.textSecondaryColor,
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          '&:hover': { 
                            borderColor: theme.primaryColor, 
                            color: theme.primaryColor,
                            background: `${theme.primaryColor}08`
                          }
                        }}
                      >
                        {reportGuidanceLoading ? 'Loading...' : 'Reset'}
                      </Button>
                      <Button 
                        variant="contained" 
                        onClick={() => updateReportGuidance(reportGuidanceData.guidance)}
                        disabled={reportGuidanceSaving || !reportGuidanceData.guidance.trim()}
                        startIcon={<SaveIcon />}
                        sx={{
                          background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.linkHoverColor})`,
                          borderRadius: 2,
                          px: 3,
                          py: 1.5,
                          fontWeight: 600,
                          boxShadow: `0 4px 16px ${theme.primaryColor}40`,
                          '&:hover': { 
                            background: `linear-gradient(135deg, ${theme.linkHoverColor}, ${theme.primaryColor})`,
                            boxShadow: `0 6px 20px ${theme.primaryColor}60`
                          }
                        }}
                      >
                        {reportGuidanceSaving ? 'Saving...' : 'Save Guidance'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Box>

        {/* Document Management Section */}
        <Box sx={{ width: '100%' }}>
          <DocumentManager />
        </Box>

        {/* App Settings */}
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card sx={{ ...cardStyle, p: 0, height: '100%' }}>
            <Accordion 
              expanded={true}
              sx={{ 
                boxShadow: 'none', 
                '&:before': { display: 'none' }, 
                background: 'transparent',
                height: '100%',
                '& .MuiAccordionSummary-root': {
                  px: 3,
                  py: 2,
                  background: `linear-gradient(135deg, ${theme.primaryColor}08, ${theme.secondaryColor}08)`,
                  cursor: 'default'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dynamicTheme.textColor }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.primaryColor}, ${theme.linkHoverColor})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${theme.primaryColor}30`
                  }}>
                    <AppSettingsAltIcon sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                    Application Settings
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <TextField
                      fullWidth
                      label="Application Name"
                      value={state.app.name}
                      onChange={(e) => actions.updateApp({ name: e.target.value })}
                      helperText="The name displayed in the header"
                      sx={{
                        '& .MuiInputLabel-root': { 
                          color: dynamicTheme.textSecondaryColor,
                          fontWeight: 500
                        },
                        '& .MuiInputBase-input': { 
                          color: dynamicTheme.textColor,
                          fontSize: 16
                        },
                        '& .MuiFormHelperText-root': { 
                          color: dynamicTheme.textSecondaryColor,
                          fontSize: 13
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: dynamicTheme.borderColor,
                          borderWidth: 2
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                          borderWidth: 2
                        }
                      }}
                    />
                    <TextField
                      fullWidth
                      label="Logo URL"
                      value={state.app.logo}
                      onChange={(e) => actions.updateApp({ logo: e.target.value })}
                      helperText="Path to the logo image"
                      sx={{
                        '& .MuiInputLabel-root': { 
                          color: dynamicTheme.textSecondaryColor,
                          fontWeight: 500
                        },
                        '& .MuiInputBase-input': { 
                          color: dynamicTheme.textColor,
                          fontSize: 16
                        },
                        '& .MuiFormHelperText-root': { 
                          color: dynamicTheme.textSecondaryColor,
                          fontSize: 13
                        },
                        '& .MuiOutlinedInput-notchedOutline': { 
                          borderColor: dynamicTheme.borderColor,
                          borderWidth: 2
                        },
                        '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor
                        },
                        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                          borderColor: theme.primaryColor,
                          borderWidth: 2
                        }
                      }}
                    />
                                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'center' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, justifyContent: 'center', width: '100%' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ color: dynamicTheme.textSecondaryColor, fontWeight: 500 }}>
                            Logo Preview
                          </Typography>
                          <Box 
                            sx={{ 
                              width: 80, 
                              height: 80, 
                              border: `2px solid ${dynamicTheme.borderColor}`,
                              borderRadius: 2,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              background: dynamicTheme.cardBackground,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                            }}
                          >
                            <img 
                              src={state.app.logo} 
                              alt="Logo" 
                              style={{ maxWidth: '60px', maxHeight: '60px' }}
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                            <Box 
                              sx={{ 
                                display: 'none',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '100%',
                                height: '100%',
                                color: dynamicTheme.textSecondaryColor,
                                fontSize: 12,
                                textAlign: 'center'
                              }}
                            >
                              No Image
                            </Box>
                          </Box>
                        </Box>
                        <Divider orientation="vertical" flexItem sx={{ borderColor: dynamicTheme.borderColor, height: 100 }} />
                        <Box sx={{ display: 'flex', gap: 2 }}>
                          <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel sx={{ 
                              color: dynamicTheme.textSecondaryColor, 
                              fontWeight: 500 
                            }}>
                              Language
                            </InputLabel>
                            <Select
                              value={state.app.language}
                              label="Language"
                              onChange={(e) => actions.updateApp({ language: e.target.value })}
                              sx={{
                                color: dynamicTheme.textColor,
                                '& .MuiOutlinedInput-notchedOutline': { 
                                  borderColor: dynamicTheme.borderColor,
                                  borderWidth: 2
                                },
                                '& .MuiSvgIcon-root': { color: dynamicTheme.textSecondaryColor },
                                '& .MuiSelect-select': { fontSize: 16 }
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    background: dynamicTheme.cardBackground,
                                    border: `1px solid ${dynamicTheme.borderColor}`,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                                  }
                                }
                              }}
                            >
                              {languages.map((lang) => (
                                <MenuItem key={lang.value} value={lang.value} sx={{
                                  color: dynamicTheme.textColor,
                                  '&:hover': { background: theme.hoverColor },
                                  '&.Mui-selected': { 
                                    background: `${theme.primaryColor}20`,
                                    '&:hover': { background: `${theme.primaryColor}30` }
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <span style={{ fontSize: 18 }}>{lang.flag}</span>
                                    <Typography sx={{ fontWeight: 500 }}>{lang.label}</Typography>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <FormControl sx={{ minWidth: 150 }}>
                            <InputLabel sx={{ 
                              color: dynamicTheme.textSecondaryColor, 
                              fontWeight: 500 
                            }}>
                              Timezone
                            </InputLabel>
                            <Select
                              value={state.app.timezone}
                              label="Timezone"
                              onChange={(e) => actions.updateApp({ timezone: e.target.value })}
                              sx={{
                                color: dynamicTheme.textColor,
                                '& .MuiOutlinedInput-notchedOutline': { 
                                  borderColor: dynamicTheme.borderColor,
                                  borderWidth: 2
                                },
                                '& .MuiSvgIcon-root': { color: dynamicTheme.textSecondaryColor },
                                '& .MuiSelect-select': { fontSize: 16 }
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    background: dynamicTheme.cardBackground,
                                    border: `1px solid ${dynamicTheme.borderColor}`,
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                                  }
                                }
                              }}
                            >
                              {timezones.map((tz) => (
                                <MenuItem key={tz.value} value={tz.value} sx={{
                                  color: dynamicTheme.textColor,
                                  '&:hover': { background: theme.hoverColor },
                                  '&.Mui-selected': { 
                                    background: `${theme.primaryColor}20`,
                                    '&:hover': { background: `${theme.primaryColor}30` }
                                  }
                                }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <AccessTimeIcon sx={{ fontSize: 18, color: theme.primaryColor }} />
                                    <Box>
                                      <Typography sx={{ fontWeight: 500 }}>{tz.label}</Typography>
                                      <Typography variant="caption" sx={{ color: dynamicTheme.textSecondaryColor }}>
                                        {tz.offset}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Box>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Box>

        {/* Theme Settings */}
        <Box sx={{ flex: '1 1 300px', minWidth: 300 }}>
          <Card sx={{ ...cardStyle, p: 0, height: '100%' }}>
            <Accordion 
              expanded={true}
              sx={{ 
                boxShadow: 'none', 
                '&:before': { display: 'none' }, 
                background: 'transparent',
                height: '100%',
                '& .MuiAccordionSummary-root': {
                  px: 3,
                  py: 2,
                  background: `linear-gradient(135deg, ${theme.secondaryColor}08, ${theme.primaryColor}08)`,
                  cursor: 'default'
                },
                '& .MuiAccordionSummary-expandIconWrapper': {
                  display: 'none'
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: dynamicTheme.textColor }} />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: `linear-gradient(135deg, ${theme.secondaryColor}, ${theme.primaryColor})`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 4px 12px ${theme.secondaryColor}30`
                  }}>
                    <PaletteIcon sx={{ color: '#fff', fontSize: 20 }} />
                  </Box>
                  <Typography variant="h6" fontWeight={700} sx={{ color: dynamicTheme.textColor }}>
                    Theme & Colors
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <Box sx={{ width: '80%', display: 'flex', flexDirection: 'column', gap: 3 }}>
                    <FormControl fullWidth>
                      <InputLabel sx={{ 
                        color: dynamicTheme.textSecondaryColor, 
                        fontWeight: 500 
                      }}>
                        Theme Mode
                      </InputLabel>
                      <Select
                        value={state.theme.mode}
                        label="Theme Mode"
                        onChange={(e) => {
                          const newMode = e.target.value;
                          actions.updateTheme({ mode: newMode });
                          
                          // Update colors based on mode
                          if (newMode === 'dark') {
                            actions.updateTheme({
                              mode: newMode,
                              backgroundColor: '#1a1a1a',
                              cardBackground: '#2d2d2d',
                              textColor: '#ffffff',
                              textSecondaryColor: '#b0b0b0',
                              borderColor: '#404040',
                              searchBarColor: '#3d3d3d',
                              tableHeaderColor: '#333333',
                            });
                          } else if (newMode === 'light') {
                            actions.updateTheme({
                              mode: newMode,
                              backgroundColor: '#f8f9fb',
                              cardBackground: '#fff',
                              textColor: '#222',
                              textSecondaryColor: '#b0b7c3',
                              borderColor: '#ececec',
                              searchBarColor: '#f5f6fa',
                              tableHeaderColor: '#f8f9fb',
                            });
                          }
                        }}
                        sx={{
                          color: dynamicTheme.textColor,
                          '& .MuiOutlinedInput-notchedOutline': { 
                            borderColor: dynamicTheme.borderColor,
                            borderWidth: 2
                          },
                          '& .MuiSvgIcon-root': { color: dynamicTheme.textSecondaryColor },
                          '& .MuiSelect-select': { fontSize: 16 }
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              background: dynamicTheme.cardBackground,
                              border: `1px solid ${dynamicTheme.borderColor}`,
                              boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
                            }
                          }
                        }}
                      >
                        <MenuItem value="light" sx={{
                          color: dynamicTheme.textColor,
                          '&:hover': { background: theme.hoverColor },
                          '&.Mui-selected': { 
                            background: `${theme.primaryColor}20`,
                            '&:hover': { background: `${theme.primaryColor}30` }
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              background: '#f8f9fb',
                              border: '2px solid #ececec'
                            }} />
                            <Typography sx={{ fontWeight: 500 }}>Light</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="dark" sx={{
                          color: dynamicTheme.textColor,
                          '&:hover': { background: theme.hoverColor },
                          '&.Mui-selected': { 
                            background: `${theme.primaryColor}20`,
                            '&:hover': { background: `${theme.primaryColor}30` }
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              background: '#1a1a1a',
                              border: '2px solid #404040'
                            }} />
                            <Typography sx={{ fontWeight: 500 }}>Dark</Typography>
                          </Box>
                        </MenuItem>
                        <MenuItem value="auto" sx={{
                          color: dynamicTheme.textColor,
                          '&:hover': { background: theme.hoverColor },
                          '&.Mui-selected': { 
                            background: `${theme.primaryColor}20`,
                            '&:hover': { background: `${theme.primaryColor}30` }
                          }
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ 
                              width: 20, 
                              height: 20, 
                              borderRadius: '50%', 
                              background: 'linear-gradient(135deg, #f8f9fb 50%, #1a1a1a 50%)',
                              border: '2px solid #ececec'
                            }} />
                            <Typography sx={{ fontWeight: 500 }}>Auto</Typography>
                          </Box>
                        </MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </Card>
        </Box>
      </Box>
    </Box>
  );
} 